import type {
  RecentShipment,
  RecentShipmentsData,
} from '../../utils/client-reports/types';
import {
  EmptySection,
  EtaStatus,
  ReportTable,
  SectionHeader,
  StatusText,
} from './ReportBlocks';
import { titleCase } from './reportLayout';
import { useReportFormat } from './useReportFormat';

// Últimos embarques despachados: lo que zarpó en la última semana con zarpes.
const RecentShipmentsView = ({
  number: sectionNumber,
  data,
}: {
  number: number;
  data: RecentShipmentsData;
}) => {
  const { t, number, date, dayRange } = useReportFormat();
  const title = t('Últimos embarques despachados');

  if (data.week == null || data.rows.length === 0) {
    return (
      <section className='flex flex-col gap-6'>
        <SectionHeader number={sectionNumber} title={title} />
        <EmptySection>
          {data.week == null
            ? t('Todavía no hay embarques zarpados esta temporada.')
            : t('No hubo zarpes en la semana {{week}}.', {
                week: String(data.week).padStart(2, '0'),
              })}
        </EmptySection>
      </section>
    );
  }

  return (
    <section className='flex flex-col gap-8'>
      <SectionHeader
        number={sectionNumber}
        title={title}
        aside={t(
          'Zarpes de la semana {{week}} ({{range}}) · {{shipments}} embarques · {{ceq}} cajas eq.',
          {
            week: String(data.week).padStart(2, '0'),
            range: data.start && data.end ? dayRange(data.start, data.end) : '',
            shipments: number(data.totals.shipments),
            ceq: number(data.totals.ceq),
          }
        )}
      />
      <ReportTable<RecentShipment>
        label={title}
        rows={data.rows}
        rowKey={(row) => row.reference}
        columns={[
          {
            key: 'identifier',
            label: t('Contenedor / AWB'),
            nowrap: true,
            render: (row) => (
              <span className='font-medium'>
                {row.identifiers.join(', ') || row.reference}
              </span>
            ),
          },
          {
            key: 'vessel',
            label: t('Nave / vuelo'),
            render: (row) => row.vessel || '—',
          },
          {
            key: 'destination',
            label: t('Puerto de destino'),
            render: (row) =>
              row.destination ? titleCase(row.destination) : '—',
          },
          {
            key: 'etd',
            label: 'ETD',
            nowrap: true,
            render: (row) => date(row.etd),
          },
          {
            key: 'eta',
            label: 'ETA',
            nowrap: true,
            render: (row) => date(row.eta),
          },
          {
            key: 'status',
            label: t('Estado'),
            render: (row) =>
              row.arrived ? (
                <StatusText tone='arrived'>{t('Arribado')}</StatusText>
              ) : (
                <EtaStatus daysToEta={row.daysToEta} />
              ),
          },
          {
            key: 'ceq',
            label: t('Cajas eq.'),
            align: 'right',
            render: (row) => number(row.ceq),
          },
        ]}
        total={{
          identifier: t('Total · {{total}} embarques', {
            total: number(data.totals.shipments),
          }),
          ceq: number(data.totals.ceq),
        }}
      />
    </section>
  );
};

export default RecentShipmentsView;
